import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { FileUploader } from "@/components/FileUploader";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RupiahInput } from "@/components/RupiahInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function NewRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const [divisi, setDivisi] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [vehicleRefs, setVehicleRefs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/me").then((r) => setDivisi(r.data.user.divisi || "")).catch(() => {});
  }, []);

  useEffect(() => {
    const st = location.state;
    if (st) {
      if (st.prefillAmount) setAmount(String(st.prefillAmount));
      if (st.prefillReason) setReason(st.prefillReason);
      if (st.vehicleRefs) setVehicleRefs(st.vehicleRefs);
    }
  }, [location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const amt = Number(amount);
    if (!amt || amt <= 0) { setError("Jumlah harus lebih dari 0"); return; }
    setLoading(true);
    try {
      await api.post("/cash-requests", {
        divisi, amount_requested: amt, reason,
        need_proof_file_ids: files.map((f) => f.file_id),
        vehicle_refs: vehicleRefs,
      });
      toast.success("Pengajuan kas berhasil dikirim ke antrian approval.");
      navigate("/requests");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline">
        <ArrowLeft className="h-4 w-4" /> Kembali
      </button>
      <div>
        <h2 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Buat Pengajuan Kas</h2>
        <p className="text-sm text-muted-foreground">Isi detail permintaan dan lampirkan bukti kebutuhan.</p>
      </div>

      <Card className="p-6">
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="divisi">Divisi</Label>
            <Input id="divisi" value={divisi} onChange={(e) => setDivisi(e.target.value)} required
              placeholder="cth: Marketing" className="mt-1.5" data-testid="request-divisi-input" />
          </div>
          <div>
            <Label htmlFor="amount">Jumlah Diminta (Rp)</Label>
            <RupiahInput id="amount" value={amount} onChange={(e) => setAmount(e.target.value)} required
              placeholder="cth: 5000000" className="mt-1.5 font-mono" data-testid="request-amount-input" />
            {amount && <p className="text-xs text-muted-foreground mt-1">{formatRupiah(amount)}</p>}
          </div>
          <div>
            <Label htmlFor="reason">Alasan Penggunaan</Label>
            <Textarea id="reason" value={reason} onChange={(e) => setReason(e.target.value)} required rows={3}
              placeholder="Jelaskan keperluan penggunaan kas..." className="mt-1.5" data-testid="request-reason-input" />
          </div>
          <div>
            <Label>Bukti Kebutuhan (opsional)</Label>
            <div className="mt-1.5">
              <FileUploader files={files} onChange={setFiles} testId="need-proof-uploader" />
            </div>
          </div>
          {error && <p data-testid="request-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-brand hover:bg-emerald-800 h-11" data-testid="submit-cash-request-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Kirim Pengajuan</>}
          </Button>
        </form>
      </Card>
    </div>
  );
}
